import Messenger from '../../common/messenger';
import { DocType } from '../../common/Constant';
import DeferQueue from '../../utils/DeferQueue';
import log from 'loglevel';
import { DocEvent } from '../EventEnum';
import { createElement, removeFromParent } from './../../utils/Dom';
import AbstractAdapter from './AbstractAdapter';

// 私有化方法
// const privateAdapterFire = Symbol('privateAdapterFire');
const IFramePrefix = 'vh-iframe';

/**
 * @description 文档PPT适配器，适配器装载了PPT导出的H5页面
 * @export
 * @class DocAdapter
 */
export default class DocAdapter extends AbstractAdapter {
  constructor() {
    super();
    this._ext = 'pptx';
    this.prevPageId = -1;
    this.inited = false;
  }

  init(opts = {}) {
    super.init(opts);

    this._docType = DocType.H5;
    this._info.docType = this._docType;
    this._info.hash = opts.hash;
    this._info.slideIndex = opts.slideIndex || 0;
    this._info.slidesTotal = opts.slidesTotal;
    this._info.stepIndex = opts.stepIndex || 0;
    this._info.stepsTotal = opts.stepsTotal || 0;
    this._info.pageHash = `${this.info.hash}/${this.info.slideIndex}` || '';
    this._info.docId = opts['docId'] || opts['document_id'] || '';

    if (!this.msger) {
      // messageName, projectName
      this.msger = new Messenger('parent', `vhall-paas-doc-${this.info.docId}`);
      this.msger.listen(msg => {
        this.fromIFrame(msg);
      });
    }

    this.store.setKV('docType', 1);
    let isVod = this.store.getKV('isVod');
    // 点播数据中不需要进行延迟处理
    this._deferQueue = new DeferQueue(isVod ? 0 : 350);
  }

  /**
   * @description 注册Iframe 对象给消息收发器
   * @param {*} iframe iframe 实例
   * @param {*} name 名字，iframeName
   * @memberof DocAdapter
   */
  addIFrameTarget(iframe, name) {
    log.info(`register iframe to Messager, name: ${name}`);
    this.msger.addTarget(iframe.contentWindow, name);
  }

  createEl(elId, uid) {
    super.createEl(elId);
    let el = document.getElementById(elId);
    let iframe = createElement(
      'iFrame',
      {
        id: IFramePrefix + '-' + uid,
        scrolling: 'no'
        // src: "./ppt1/presentation.html"
        // src: "https://t-static01-open.e.vhall.com/document/5cb636706d17add5bdb975426f2e9887/presentation.html"
        // src: "https://t-static01-open.e.vhall.com/document/05c700003db1885e28a4f3d9c9265f52/presentation.html"
      },
      {
        style: 'display:block;position: absolute;border:none; width: 100%; height: 100%;'
      }
    );
    el.appendChild(iframe);

    this.iframe = iframe;
    // iframe创建完成后，将其添加到doc对象里并置空其load事件
    iframe.onload = () => {
      this.addIFrameTarget(iframe, 'presentation');
      iframe.onload = null;
    };
    this.addIFrameTarget(iframe, 'presentation');
    this.node = iframe;
  }

  /**
   * 下一页PPT
   *
   * @memberof DocAdapter
   */
  nextPage() {
    log.info('DocAdapter nextPage');
    super.nextPage();
    if (this.prevPageId === this.currentPageId) {
      return;
    }

    let senderId = this.store.getKV('sender');
    this.prevPageId = this.currentPageId;
    let p = {
      event: 'nextSlide',
      params: '',
      senderId
    };

    this.privateAdapterFire(p);
  }

  /**
   * 上一页PPT
   *
   * @memberof DocAdapter
   */
  prevPage() {
    log.info('DocAdapter prevPage');
    super.prevPage();
    if (this.prevPageId === this.currentPageId) {
      return;
    }

    let senderId = this.store.getKV('sender');
    this.prevPageId = this.currentPageId;
    let p = {
      event: 'preSlide',
      params: '',
      senderId
    };

    this.privateAdapterFire(p);
  }

  /**
   * 跳转到第N页
   *
   * @param {*} id
   * @memberof DocAdapter
   */
  gotoPage(id) {
    log.info(`DocAdapter gotoPage, ${id}`);
    super.gotoPage(id);
    if (this.prevPageId === this.currentPageId) {
      return;
    }

    let senderId = this.store.getKV('sender');
    this.prevPageId = this.currentPageId;
    let p = {
      event: 'jumpSlide',
      params: this.currentPageId,
      senderId
    };
    console.log('gotoPage方法向内部iframe发送信息:', p);
    this.privateAdapterFire(p);
  }

  /**
   * 跳转到某一页的某一步
   *
   * @param {*} args
   * @memberof DocAdapter
   */
  gotoStep(...args) {
    log.info(`DocAdapter gotoStep, slideIndex: ${args[0]}, stepIndex: ${args[1]}`);
    if (args[0] === this.info.slideIndex && args[1] === this.info.stepIndex) {
      return;
    }

    let pageId = Math.limitIn(args[0], 0, this.info.slidesTotal - 1);
    this.info.slideIndex = pageId;
    this.info.stepIndex = args[1];
    this.store.setKV('info', this.info);
    let senderId = this.store.getKV('sender');
    let p = {
      event: 'jumpStep',
      params: {
        slideIndex: pageId,
        stepIndex: args[1]
      },
      senderId
    };

    this.privateAdapterFire(p);
  }

  /**
   * 当前页的下一步
   *
   * @memberof DocAdapter
   */
  nextStep() {
    log.info('DocAdapter nextStep');
    let senderId = this.store.getKV('sender');
    let p = {
      event: 'nextStep',
      params: '',
      senderId
    };
    this.privateAdapterFire(p);
  }

  /**
   * 上一步
   *
   * @memberof DocAdapter
   */
  prevStep() {
    log.info('DocAdapter prevStep');
    let senderId = this.store.getKV('sender');
    let p = {
      event: 'preStep',
      params: '',
      senderId
    };
    this.privateAdapterFire(p);
  }

  /**
   * 播放
   *
   * @memberof DocAdapter
   */
  play() {
    log.info('DocAdapter play');
    let senderId = this.store.getKV('sender');
    let p = {
      event: 'play',
      params: '',
      senderId
    };
    this.privateAdapterFire(p);
  }

  /**
   * 暂停
   *
   * @memberof DocAdapter
   */
  pause() {
    log.info('DocAdapter pause');
    let senderId = this.store.getKV('sender');
    let p = {
      event: 'pause',
      params: '',
      senderId
    };
    this.privateAdapterFire(p);
  }

  /**
   * 设置舞台大小
   *
   * @param {*} w
   * @param {*} h
   * @memberof DocAdapter
   */
  resize(w, h) {
    log.info(`DocAdapter resize, w: ${w}, h: ${h}`);
    super.resize(w, h);
    let senderId = this.store.getKV('sender');
    let p = {
      event: 'setSize',
      params: {
        width: w,
        height: h
      },
      senderId
    };
    this.privateAdapterFire(p);
  }

  /**
   * 销毁函数
   *
   * @memberof DocAdapter
   * 移除iframe必须手动释放内存，否则在ie和安卓机上频繁切换会卡死
   * 不同域名的iframe因同源限制无法选中，故无法用以下方式干净的清除
   *    this.node.contentWindow.document.write('');
   *    this.node.contentWindow.document.clear();
   *    this.node.contentWindow.close();
   */
  destroy() {
    super.destroy();

    if (this.msger) {
      this.msger.clear();
      this.msger = null;
    }

    if (this.node) {
      try {
        this.node.src = 'about:blank';
      } catch (e) {
        console.log(e);
      }

      removeFromParent(this.node);
    }

    this.inited = false;
  }

  fromIFrame(msg) {
    log.info(`DocAdapter fromIFrame, ${msg}`);
    // FIXME: 如果加载多个文档，每个文档都会派发消息出来，无法判断是从哪个iframe内出来的。需要处理

    // 因为 iframe传来的消息，只有｛event, params｝ 缺少所属的id字段， 给他补上
    let result = {
      cid: this.cid,
      stamp: Date.now().toString(),
      doc_type: this.store.getKV('docType') || 1
    };

    if (typeof msg === 'string') {
      Object.assign(result, JSON.parse(msg));
    }
    let { event, params } = result;
    // 如果文档id不同，就不是这个iframe发来的消息
    if (this.info.docId != params['docId']) return;
    switch (event) {
      case DocEvent.READY:
        /*
        docId: "asd"
        hash: "8fc89be4e6bd4e0bf7fd0f1aacf9d807"
        originHeight: 1080
        originWidth: 1920
        s: "1567518802754"
        slideIndex: 0
        slidesTotal: 38
        stepIndex: 0
        stepsTotal: 1
        userId: "123"
        version: "2.0.0"
      */
        Object.assign(this._info, params);
        this._initCompleteCallBK && this._initCompleteCallBK();
        break;
      case DocEvent.STEP_CHANGE:
      case DocEvent.PAGE_CHANGE:
        Object.assign(this._info, params);
        // 翻步触发翻页的时候，并没有像上下一页记录prevPageId,所以在对应的翻页成功事件里记录
        this.prevPageId = params.slideIndex;
        break;
      default:
        break;
    }

    this.store.setKV('info', this.info);
    let sender = params.senderId;
    const { userId: selfId } = this.store.getKV('passThrough');

    // 如果是自己触发的消息，再发送翻页，翻步数据出去
    if (sender == selfId || event == DocEvent.READY || event == DocEvent.ANIMATIONINFO || sender == '') {
      this._ee.dispatch('doc_events', result);
    }

    const CARE_EVENTS = [DocEvent.PAGE_CHANGE, DocEvent.STEP_CHANGE];
    // 翻页日志上报事件需要派发出去
    if (CARE_EVENTS.indexOf(event) >= 0) {
      this._ee.dispatch('report_doc_events', result);
    }
  }

  set hash(v) {
    if (v == 1) {
      this._ee.dispatch('docNotExit', {
        docId: this.info.docId,
        cid: this.store.info.cid
      });
      return;
    }
    super.hash = v;
    // load doc
    const { docId, userId } = this.info;
    // 添加sender，某些情况下sender与userId不一致，代表文档初始化的文档消息不需要发送
    const sender = this.store.getKV('sender') || userId;
    // this.node.src = `https://localhost:9090/presentation2.html?docId=${docId}&userId=${userId}&s=${Date.now()}&sender=${sender}`;
    this.node.src = `${
      this.opts.urlBase
    }${v}/presentation2.html?docId=${docId}&userId=${userId}&s=${Date.now()}&sender=${sender}`;
  }

  get hash() {
    return super.hash;
  }

  /**
   *
   * @description 私有函数进行消息发送
   * @param {*} o
   * @memberof DocAdapter
   */
  privateAdapterFire(o) {
    if (this._selected || !this.inited) {
      const send = data => this.msger.send(data);
      this._deferQueue.push(JSON.stringify(o), send);
    }
  }

  setSelected(val) {
    super.setSelected(val);
    this._selected = val;
  }
}
